import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import {
  Eye,
  Heart,
  Users,
  Bookmark,
  MessageSquare,
  Flame,
  Diamond,
  BookOpen,
  Image,
  Tv,
  FileText,
  TrendingUp,
  ArrowLeft,
  BarChart3,
  Loader2,
  Trophy,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = '/api';

/* ── tiny helpers ────────────────────────────────────── */
const fmt = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const pct = (part, total) => (total === 0 ? 0 : Math.round((part / total) * 100));

/* ── Stat card ───────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="bg-[#111] border-gray-800/60 hover:border-gray-700 transition-colors" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight" data-testid={`${testId}-value`}>{fmt(value)}</p>
      <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</p>
    </CardContent>
  </Card>
);

/* ── Horizontal bar ──────────────────────────────────── */
const Bar = ({ label, value, max, color, icon: Icon }) => {
  const w = max === 0 ? 0 : Math.round((value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
      <span className="text-sm text-gray-400 w-20 shrink-0 truncate">{label}</span>
      <div className="flex-1 bg-gray-800 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${color}`}
          style={{ width: `${w}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-white w-12 text-right">{fmt(value)}</span>
    </div>
  );
};

/* ── Top content item row ────────────────────────────── */
const TopItem = ({ item, rank }) => (
  <div className="flex items-center gap-4 py-3 border-b border-gray-800/40 last:border-0" data-testid={`top-item-${rank}`}>
    <span className="text-lg font-black text-gray-600 w-6 text-center">{rank}</span>
    {item.thumbnail ? (
      <img src={item.thumbnail} alt="" className="w-10 h-10 rounded object-cover bg-gray-800" />
    ) : (
      <div className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center">
        <Image className="w-4 h-4 text-gray-600" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{item.title}</p>
      <p className="text-xs text-gray-500">
        {fmt(item.views)} views &middot; {fmt(item.saves)} saves &middot; {fmt(item.reactions_total)} reactions
      </p>
    </div>
    <div className="text-right shrink-0">
      <p className="text-sm font-bold text-orange-400">{item.engagement_score}</p>
      <p className="text-[10px] text-gray-600 uppercase">score</p>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════ */
const CreatorAnalytics = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [topContent, setTopContent] = useState(null);
  const [reactions, setReactions] = useState(null);
  const [saves, setSaves] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentType, setContentType] = useState('series');
  const [days, setDays] = useState(0); // 0 = all time, 7, 30

  const token = localStorage.getItem('token');

  const headers = { Authorization: `Bearer ${token}` };

  /* ── guard ─────────────────────────────────────────── */
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    const ok = user.is_creator || ['creator', 'artist', 'author'].includes(user.role);
    if (!ok) { toast.error('Creator access only'); navigate('/'); }
  }, [user, authLoading, navigate]);

  /* ── data fetching ─────────────────────────────────── */
  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const dq = days > 0 ? `?days=${days}` : '';
      const [s, r, sv] = await Promise.all([
        axios.get(`${API}/creator/analytics/summary${dq}`, { headers }),
        axios.get(`${API}/creator/analytics/reactions${dq}`, { headers }),
        axios.get(`${API}/creator/analytics/saves${dq}`, { headers }),
      ]);
      setSummary(s.data);
      setReactions(r.data);
      setSaves(sv.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── top content (depends on contentType + days) ───── */
  const fetchTopContent = useCallback(async () => {
    if (!token) return;
    try {
      const dq = days > 0 ? `&days=${days}` : '';
      const res = await axios.get(`${API}/creator/analytics/top-content?type=${contentType}&limit=10${dq}`, { headers });
      setTopContent(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [token, contentType, days]);

  useEffect(() => { fetchTopContent(); }, [fetchTopContent]);

  /* ── loading state ─────────────────────────────────── */
  if (authLoading || loading || !summary) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="analytics-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const contentTypeLabels = { series: 'Series', artwork: 'Artwork', episode: 'Episodes', novel: 'Novels' };
  const contentTypeIcons = { series: BookOpen, artwork: Image, episode: Tv, novel: FileText };

  const reactionMax = Math.max(reactions?.heart || 0, reactions?.fire || 0, reactions?.diamond || 0, 1);
  const saveMax = Math.max(saves?.artwork || 0, saves?.series || 0, saves?.episode || 0, saves?.novel || 0, 1);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="creator-analytics-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="border-b border-gray-800/60 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <BarChart3 className="w-6 h-6 text-orange-500" />
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics</h1>
              </div>
              <p className="text-sm text-gray-500">Your content performance at a glance</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Period selector */}
              <div className="flex rounded-lg border border-gray-800 overflow-hidden" data-testid="period-selector">
                {[{ label: '7d', value: 7 }, { label: '30d', value: 30 }, { label: 'All', value: 0 }].map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setDays(p.value)}
                    className={`px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      days === p.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-transparent text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                    data-testid={`period-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="border-gray-800 text-gray-400 hover:text-white hover:border-gray-600" data-testid="back-to-dashboard">
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-8">
        {/* ── Summary cards ─────────────────────────────── */}
        <section data-testid="summary-section">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={Eye}       label={days > 0 ? "Views (all time)" : "Views"} value={summary.total_views}     accent="bg-blue-500/10 text-blue-400"   testId="stat-views" />
            <StatCard icon={Users}     label="Followers" value={summary.total_followers} accent="bg-green-500/10 text-green-400" testId="stat-followers" />
            <StatCard icon={Heart}     label="Reactions"  value={summary.total_reactions} accent="bg-pink-500/10 text-pink-400"  testId="stat-reactions" />
            <StatCard icon={Bookmark}  label="Saves"     value={summary.total_saves}     accent="bg-amber-500/10 text-amber-400" testId="stat-saves" />
            <StatCard icon={MessageSquare} label="Comments" value={summary.total_comments} accent="bg-purple-500/10 text-purple-400" testId="stat-comments" />
          </div>
        </section>

        {/* ── Content counts (secondary) ───────────────── */}
        <section data-testid="content-counts-section">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Your Content</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={BookOpen}  label="Series"   value={summary.total_series}   accent="bg-orange-500/10 text-orange-400" testId="stat-series" />
            <StatCard icon={Tv}        label="Episodes" value={summary.total_episodes} accent="bg-cyan-500/10 text-cyan-400"    testId="stat-episodes" />
            <StatCard icon={Image}     label="Artworks" value={summary.total_artworks} accent="bg-rose-500/10 text-rose-400"    testId="stat-artworks" />
            <StatCard icon={FileText}  label="Novels"   value={summary.total_novels}   accent="bg-indigo-500/10 text-indigo-400" testId="stat-novels" />
          </div>
        </section>

        {/* ── Two-column: Top Content + Breakdowns ────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Top Content (3/5) */}
          <section className="lg:col-span-3" data-testid="top-content-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Top Content</h2>
              </div>
              {/* Content type selector */}
              <div className="relative">
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="appearance-none bg-[#111] border border-gray-800 text-sm text-gray-300 rounded-lg pl-3 pr-8 py-1.5 focus:outline-none focus:border-orange-500/50 cursor-pointer"
                  data-testid="content-type-selector"
                >
                  {Object.entries(contentTypeLabels).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <Card className="bg-[#111] border-gray-800/60">
              <CardContent className="p-4 sm:p-5">
                {topContent?.items?.length > 0 ? (
                  <div data-testid="top-content-list">
                    {topContent.items.map((item, i) => (
                      <TopItem key={item.id} item={item} rank={i + 1} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-600" data-testid="top-content-empty">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No {contentTypeLabels[contentType]?.toLowerCase()} content yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Right: Breakdowns (2/5) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Reaction breakdown */}
            <section data-testid="reactions-breakdown">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Reaction Breakdown</h2>
              <Card className="bg-[#111] border-gray-800/60">
                <CardContent className="p-4 sm:p-5 space-y-3">
                  {reactions && reactions.total > 0 ? (
                    <>
                      <Bar label="Heart"   value={reactions.heart}   max={reactionMax} color="bg-pink-500"   icon={Heart} />
                      <Bar label="Fire"    value={reactions.fire}    max={reactionMax} color="bg-orange-500" icon={Flame} />
                      <Bar label="Diamond" value={reactions.diamond} max={reactionMax} color="bg-cyan-400"   icon={Diamond} />
                      <div className="pt-2 border-t border-gray-800/40 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Total reactions</span>
                        <span className="text-sm font-bold text-white" data-testid="reactions-total">{fmt(reactions.total)}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 text-center py-6" data-testid="reactions-empty">No reactions yet</p>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Save breakdown */}
            <section data-testid="saves-breakdown">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Save Breakdown</h2>
              <Card className="bg-[#111] border-gray-800/60">
                <CardContent className="p-4 sm:p-5 space-y-3">
                  {saves && saves.total > 0 ? (
                    <>
                      <Bar label="Artwork"  value={saves.artwork}  max={saveMax} color="bg-rose-500"   icon={Image} />
                      <Bar label="Series"   value={saves.series}   max={saveMax} color="bg-orange-500"  icon={BookOpen} />
                      <Bar label="Episode"  value={saves.episode}  max={saveMax} color="bg-cyan-500"    icon={Tv} />
                      <Bar label="Novel"    value={saves.novel}    max={saveMax} color="bg-indigo-500"  icon={FileText} />
                      <div className="pt-2 border-t border-gray-800/40 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Total saves</span>
                        <span className="text-sm font-bold text-white" data-testid="saves-total">{fmt(saves.total)}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 text-center py-6" data-testid="saves-empty">No saves yet</p>
                  )}
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorAnalytics;
